/**
 * 平台检测 Hook
 * 
 * 用于检测当前运行平台，支持：
 * - 移动端/桌面端检测
 * - 原生 App 检测（Capacitor）
 * - 触屏设备检测
 * - 安全区域获取
 */

import { useState, useEffect, useCallback } from 'react'

export interface SafeAreaInsets {
  top: number
  bottom: number
  left: number
  right: number
}

export interface PlatformInfo {
  /** 是否为移动端（宽度 < 768px 或原生 App） */
  isMobile: boolean
  /** 是否为平板（768px <= 宽度 < 1024px） */
  isTablet: boolean
  /** 是否为桌面端（宽度 >= 1024px） */
  isDesktop: boolean
  /** 是否为 Capacitor 原生 App */
  isNativeApp: boolean
  /** 是否为触屏设备 */
  isTouch: boolean
  /** 是否为 iOS 设备 */
  isIOS: boolean
  /** 是否为 Android 设备 */
  isAndroid: boolean
  /** 安全区域边距（刘海屏等） */
  safeAreaInsets: SafeAreaInsets
  /** 当前视口宽度 */
  viewportWidth: number
  /** 当前视口高度 */
  viewportHeight: number
}

// 默认安全区域
const DEFAULT_SAFE_AREA: SafeAreaInsets = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
}

// 检测是否为 Capacitor 原生 App
type WindowWithCapacitor = Window & { Capacitor?: { isNativePlatform?: () => boolean } }
function detectNativeApp(): boolean {
  if (typeof window === 'undefined') return false
  // Capacitor 会在 window 上注入 Capacitor 对象
  return !!(window as WindowWithCapacitor).Capacitor?.isNativePlatform?.()
}

// 检测是否为触屏设备
function detectTouch(): boolean {
  if (typeof window === 'undefined') return false
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

// 检测 iOS
function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

// 检测 Android
function detectAndroid(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android/i.test(navigator.userAgent)
}

// 获取安全区域边距
function getSafeAreaInsets(): SafeAreaInsets {
  if (typeof window === 'undefined' || typeof getComputedStyle === 'undefined') {
    return DEFAULT_SAFE_AREA
  }
  
  const style = getComputedStyle(document.documentElement)
  
  return {
    top: parseInt(style.getPropertyValue('--sat') || '0', 10) || 
         parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--sab') || '0', 10) ||
            parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
    left: parseInt(style.getPropertyValue('--sal') || '0', 10) ||
          parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0', 10),
    right: parseInt(style.getPropertyValue('--sar') || '0', 10) ||
           parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0', 10),
  }
}

// 获取平台信息
function getPlatformInfo(): PlatformInfo {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isNativeApp: false,
      isTouch: false,
      isIOS: false,
      isAndroid: false,
      safeAreaInsets: DEFAULT_SAFE_AREA,
      viewportWidth: 1920,
      viewportHeight: 1080,
    }
  }

  const width = window.innerWidth
  const height = window.innerHeight
  const isNativeApp = detectNativeApp()
  const isTouch = detectTouch()
  const isIOS = detectIOS()
  const isAndroid = detectAndroid()

  // 移动端判断：原生 App 或宽度 < 768
  const isMobile = isNativeApp || width < 768
  // 平板判断：768 <= 宽度 < 1024
  const isTablet = !isMobile && width < 1024
  // 桌面端判断：宽度 >= 1024
  const isDesktop = width >= 1024

  return {
    isMobile,
    isTablet,
    isDesktop,
    isNativeApp,
    isTouch,
    isIOS,
    isAndroid,
    safeAreaInsets: getSafeAreaInsets(),
    viewportWidth: width,
    viewportHeight: height,
  }
}

/**
 * 平台检测 Hook
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, isNativeApp } = usePlatform()
 *   
 *   return isMobile ? <MobileView /> : <DesktopView />
 * }
 * ```
 */
export function usePlatform(): PlatformInfo {
  const [platform, setPlatform] = useState<PlatformInfo>(getPlatformInfo)

  const updatePlatform = useCallback(() => {
    setPlatform(getPlatformInfo())
  }, [])

  useEffect(() => {
    // 监听窗口大小变化
    window.addEventListener('resize', updatePlatform)
    // 监听屏幕方向变化
    window.addEventListener('orientationchange', updatePlatform)
    
    // 初始化时更新一次
    updatePlatform()

    return () => {
      window.removeEventListener('resize', updatePlatform)
      window.removeEventListener('orientationchange', updatePlatform)
    }
  }, [updatePlatform])

  return platform
}

// 导出静态方法供非 React 环境使用
export { getPlatformInfo, detectNativeApp, detectTouch, detectIOS, detectAndroid }

